import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { fetchPythonEndpoint } from 'react-query-config';

import { type ItemStatus, type Location } from '@cheqroom/api';
import { type IconKey } from '@cheqroom/icons';
import { useAuth } from '../../context/Authentication.context';

export type ItemFields = {
	[key: string]: any;
	barcodes?: string[];
	codes?: string[];
	kit?: { name: string };
	itemTypeId?: string;
	category?: { name: string };
	location?: { _id: string; name: string; icon?: IconKey; rootLocation?: Location | string | null };
	locationPath?: Location[];
	fields: Record<string, any>;
	flag?: string;
	status?: ItemStatus;
	order?: { customer?: { name?: string } | null };
	custody?: { name?: string };
};

type FetchItemFieldsResponse = ItemFields[];

interface FetchItemFieldsVariables {
	itemIds: string[];
	fieldIds: string[];
}

interface PythonFetchItemFieldsVariables {
	_fields: string;
}

export const useFetchItemFieldsQuery = <TData = FetchItemFieldsResponse, TError = unknown>(
	variables: FetchItemFieldsVariables,
	options?: UseQueryOptions<FetchItemFieldsResponse, TError, TData>
) => {
	const authUser = useAuth();
	if (!authUser?.id) {
		throw new Error('User not authenticated');
	}

	return useQuery<FetchItemFieldsResponse, TError, TData>(
		['itemFields', variables],
		fetchPythonEndpoint<FetchItemFieldsResponse, PythonFetchItemFieldsVariables>(
			`${authUser.id}/null/jwt/items/${variables.itemIds.join(',')}`,
			{ _fields: variables.fieldIds.join(',') },
			'GET'
		),
		{
			...options,
			select: (data: FetchItemFieldsResponse) => {
				if (!Array.isArray(data)) {
					return [data] as TData;
				}

				return data as TData;
			},
		}
	);
};
